  /**
 * officePptx.ts
 *
 * Reads the active PowerPoint presentation bytes via Office.js getFileAsync.
 * Ported from pptxBytesShared.js in deckorate-manifest.
 *
 * Handles:
 *   - Sequential slice reading (Office.js can't read slices in parallel)
 *   - Retry on transient "internal error" responses
 *   - Hard timeout so callers never hang forever
 */

import { logOfficeError } from "../../../telemetry";
import { readActiveOfficeFileBytes } from "../../../shared/officeFile";

export async function getActivePresentationBuffer(): Promise<ArrayBuffer> {
  const bytes = await readActiveOfficeFileBytes({
    noun: "presentation",
    label: "getActivePresentationBuffer",
  });
  return bytes.buffer as ArrayBuffer;
}

/** Select + scroll to a 1-based slide index so it becomes the active slide. */
export function goToSlide(index1Based: number): Promise<void> {
  return new Promise((resolve) => {
    try {
      Office.context.document.goToByIdAsync(
        index1Based,
        Office.GoToType.Index,
        () => resolve(),
      );
    } catch (e) {
      // Best-effort navigation only — a host that rejects goToByIdAsync
      // synchronously shouldn't block the caller, but the failure is still
      // worth tracing.
      logOfficeError(e, "goToSlide");
      resolve();
    }
  });
}

/** Best-effort "<original name> <suffix>.<extension>" — falls back to a
 * generic name when `Office.context.document.url` is unavailable (e.g. an
 * unsaved presentation), which happens on some hosts/hasn't-been-saved-yet
 * files. `suffix` may be empty for a same-name-different-extension export. */
function deriveFilenameWithSuffix(suffix: string, extension: string): string {
  try {
    const url = Office?.context?.document?.url;
    if (url) {
      const base = url.split(/[\\/]/).pop() ?? "";
      const name = base.replace(/\.pptx?$/i, "");
      if (name) return suffix ? `${name} ${suffix}.${extension}` : `${name}.${extension}`;
    }
  } catch (e) {
    // best-effort only — fall back to the generic name below, but still
    // trace the failure instead of swallowing it silently.
    logOfficeError(e, "deriveFilenameWithSuffix");
  }
  return suffix ? `Presentation ${suffix}.${extension}` : `Presentation.${extension}`;
}

export function deriveBrandedFilename(): string {
  return deriveFilenameWithSuffix("- Branded", "pptx");
}

export function deriveOptimizedFilename(): string {
  return deriveFilenameWithSuffix("- Optimised", "pptx");
}

export function derivePdfFilename(): string {
  return deriveFilenameWithSuffix("", "pdf");
}

export const PPTX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.presentationml.presentation";

/** Hands arbitrary bytes to the browser as a normal file download via a
 * throwaway Blob URL + anchor click — the add-in has no API for a native
 * "Save As" dialog, so this is the standard download mechanism shared by
 * every export/download flow in this feature set. */
export function downloadBytes(bytes: ArrayBuffer | Uint8Array, filename: string, mimeType: string): void {
  const blob = new Blob([bytes as BlobPart], { type: mimeType });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Reads the (already brand-applied, still-open) presentation's current
 * bytes and hands them to the browser as a normal file download — the
 * add-in can't script-open a second PowerPoint window, so this is the
 * closest equivalent to "save a separate branded copy" available from a
 * task pane. */
export async function downloadActivePresentationCopy(filename: string): Promise<void> {
  const buffer = await getActivePresentationBuffer();
  downloadBytes(buffer, filename, PPTX_MIME_TYPE);
}
